/**
 * Full-screen player (/listen) — very large play button, ±15s, scrubber,
 * chapters, sleep timer. Designed for the dark: minimal luminance, no
 * bright surfaces.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown, Pause, Play, RotateCcw, RotateCw, Share2, Timer,
} from "lucide-react";
import { Cover } from "../components/Cover";
import { ChapterScene } from "../components/ChapterScene";
import { ChapterSceneFullscreen } from "../components/ChapterSceneFullscreen";
import { IconButton } from "../components/ui";
import { useAudioPlayer } from "../lib/audioPlayer";
import { SLEEP_TIMER_MODES, type SleepTimerMode } from "../lib/sleepTimer";
import { buildSharePayload } from "../lib/share";
import { publicOrigin } from "../native/platform";
import { hasAnyChapterImage, toSceneChapters } from "../lib/chapterScenes";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** "9 min" while there's more than a minute left, "0:45" in the last one —
 *  a plain minute count reads fine from across the room, but the last
 *  minute is exactly when a parent wants to see it tick down to the
 *  second. */
function formatSleepRemaining(remainingS: number): string {
  if (remainingS >= 60) return `${Math.ceil(remainingS / 60)} min`;
  return `0:${Math.max(0, remainingS).toString().padStart(2, "0")}`;
}

const SLEEP_LABEL_KEYS: Record<SleepTimerMode, string> = {
  off: "player.sleepTimerOff",
  end_of_chapter: "player.sleepTimerEndOfChapter",
  end_of_story: "player.sleepTimerEndOfStory",
  "10": "player.sleepTimerMinutes",
  "20": "player.sleepTimerMinutes",
  "30": "player.sleepTimerMinutes",
};

export default function Listen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    track, isPlaying, currentTime, duration, toggle, seekTo, seekBy,
    sleepTimerMode, setSleepTimerMode, sleepDeadlineMs, currentChapter,
  } = useAudioPlayer();
  const [sleepMenuOpen, setSleepMenuOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const sleepMenuRef = useRef<HTMLDivElement | null>(null);
  const [sceneFullscreen, setSceneFullscreen] = useState(false);
  // Refocused on close, so a keyboard/screen-reader user lands back where
  // they were rather than at the top of the page.
  const expandTriggerRef = useRef<HTMLButtonElement | null>(null);
  // Only minute modes need a ticking clock — end_of_chapter/end_of_story
  // show a fixed label instead (see sleepRemainingLabel below).
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!track) navigate("/", { replace: true });
  }, [track, navigate]);

  useEffect(() => {
    if (sleepTimerMode !== "10" && sleepTimerMode !== "20" && sleepTimerMode !== "30") return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [sleepTimerMode]);

  // Close the sleep menu on a tap outside it — the mode buttons already
  // close it themselves, this is just for "changed their mind".
  useEffect(() => {
    if (!sleepMenuOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (sleepMenuRef.current && !sleepMenuRef.current.contains(e.target as Node)) {
        setSleepMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [sleepMenuOpen]);

  if (!track) return null;

  const showScenes = hasAnyChapterImage(toSceneChapters(track.chapters));

  const sleepRemainingLabel =
    sleepTimerMode === "end_of_chapter"
      ? t("player.sleepTimerEndOfChapter")
      : sleepTimerMode === "end_of_story"
        ? t("player.sleepTimerEndOfStory")
        : sleepTimerMode !== "off" && sleepDeadlineMs !== null
          ? formatSleepRemaining(Math.round((sleepDeadlineMs - nowMs) / 1000))
          : null;

  async function handleShare() {
    if (!track) return;
    const payload = buildSharePayload(track, publicOrigin());
    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch {
        // AbortError (listener cancelled the native share sheet) is the
        // expected case here. Anything else has nowhere to go either: once
        // `navigator.share` exists there's no clipboard fallback left to
        // reach for, and no error state worth showing over a share button.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(payload.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard unavailable — nothing more we can offer here */
    }
  }

  return (
    <div className="safe-top min-h-screen bg-night text-paper flex flex-col px-5 pb-10" style={{ "--safe-top-extra": "1.5rem" } as React.CSSProperties}>
      <div className="flex items-center justify-between">
        <IconButton onClick={() => navigate(-1)} aria-label={t("common.close")}>
          <ChevronDown size={20} />
        </IconButton>
        <div className="flex items-start gap-2">
          <div className="relative flex flex-col items-center">
            <IconButton onClick={handleShare} aria-label={t("player.share")}>
              <Share2 size={18} />
            </IconButton>
            {linkCopied && (
              <span className="absolute top-full mt-1 whitespace-nowrap text-[10px] text-violet-soft font-body">
                {t("player.linkCopied")}
              </span>
            )}
          </div>
          <div className="relative flex flex-col items-center" ref={sleepMenuRef}>
            <IconButton onClick={() => setSleepMenuOpen((v) => !v)} aria-label={t("player.sleepTimer")}>
              <Timer size={18} className={sleepTimerMode !== "off" ? "text-sun" : ""} />
            </IconButton>
            {sleepRemainingLabel && (
              <span className="mt-1 text-[10px] text-sun font-body tabular-nums whitespace-nowrap">
                {sleepRemainingLabel}
              </span>
            )}
            {sleepMenuOpen && (
              <div className="absolute right-0 top-11 w-52 bg-surface border border-line rounded-card overprint ink-line p-2 z-10">
                {SLEEP_TIMER_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-body ${
                      sleepTimerMode === mode ? "bg-sun text-mint-ink font-semibold" : "text-paper"
                    }`}
                    onClick={() => {
                      setSleepTimerMode(mode);
                      setSleepMenuOpen(false);
                    }}
                  >
                    {mode === "10" || mode === "20" || mode === "30"
                      ? t(SLEEP_LABEL_KEYS[mode], { count: Number(mode) })
                      : t(SLEEP_LABEL_KEYS[mode])}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-6">
        <div className="w-56 sm:w-72 md:w-96">
          {showScenes ? (
            <ChapterScene
              ref={expandTriggerRef}
              track={track}
              currentTime={currentTime}
              rounded="rounded-2xl"
              onExpand={() => setSceneFullscreen(true)}
            />
          ) : (
            <Cover
              svg={track.coverSvg}
              imageUrl={track.coverImageUrl}
              imageThumbUrl={track.coverImageThumbUrl}
              imageSize="full"
              title={track.title}
              colorway={track.coverColorway}
              colorKey={track.id}
              rounded="rounded-2xl"
            />
          )}
        </div>
        <div className="text-center">
          <h1 className="font-heading font-bold text-xl">{track.title}</h1>
          {track.subtitle && <p className="text-violet-soft text-sm mt-1">{track.subtitle}</p>}
          {currentChapter && (
            <p className="text-violet-muted text-xs mt-2 uppercase tracking-wide font-heading font-semibold">
              {currentChapter.title}
            </p>
          )}
        </div>

        <div className="w-full max-w-sm md:max-w-md flex flex-col gap-2">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={Math.min(currentTime, duration || 0)}
            onChange={(e) => seekTo(Number(e.target.value))}
            className="w-full accent-sun"
            aria-label={t("player.nowPlaying")}
          />
          <div className="flex justify-between text-xs text-violet-soft font-body tabular-nums">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <button type="button" onClick={() => seekBy(-15)} aria-label={t("player.seekBack")} className="tap-target text-paper">
            <RotateCcw size={26} />
          </button>
          <button
            type="button"
            onClick={toggle}
            aria-label={isPlaying ? t("player.pause") : t("player.play")}
            className="tap-target w-20 h-20 rounded-full bg-sun text-mint-ink overprint ink-coral flex items-center justify-center"
          >
            {isPlaying ? <Pause size={34} fill="currentColor" /> : <Play size={34} fill="currentColor" className="ml-1" />}
          </button>
          <button type="button" onClick={() => seekBy(15)} aria-label={t("player.seekForward")} className="tap-target text-paper">
            <RotateCw size={26} />
          </button>
        </div>
      </div>

      {showScenes && sceneFullscreen && (
        <ChapterSceneFullscreen
          track={track}
          currentTime={currentTime}
          duration={duration}
          chapterTitle={currentChapter?.title ?? track.title}
          isPlaying={isPlaying}
          onTogglePlay={toggle}
          onClose={() => {
            setSceneFullscreen(false);
            expandTriggerRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}
